import { ROBOT_MODES } from 'utils/constants'
import type { RobotCatalogOperation } from 'utils/selectCatalogOperation'

export const DEPLOYMENT_BLOCKED_DURING_SERVICE = 'Not available during a service action'
export const DEPLOYMENT_BLOCKED_DURING_CALIBRATION = 'Not available during service point calibration'

type OptimisticModeSwitch = {
  mode: ROBOT_MODES
  actionKey: string
} | null | undefined

function reasonForMode(mode: ROBOT_MODES | number | null | undefined): string | null {
  if (mode === ROBOT_MODES.MODE_SERVICE_POINT) {
    return DEPLOYMENT_BLOCKED_DURING_SERVICE
  }
  if (
    mode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT ||
    mode === ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT
  ) {
    return DEPLOYMENT_BLOCKED_DURING_CALIBRATION
  }
  return null
}

function reasonForCatalogOperation(operation: RobotCatalogOperation | null | undefined): string | null {
  if (!operation || operation.phase === 'idle') {
    return null
  }
  if (operation.kind === 'calibrate' || operation.kind === 'reset') {
    return DEPLOYMENT_BLOCKED_DURING_CALIBRATION
  }
  if (operation.kind === 'service') {
    return DEPLOYMENT_BLOCKED_DURING_SERVICE
  }
  return DEPLOYMENT_BLOCKED_DURING_SERVICE
}

/** Optimistic switch that is about to occupy the robot with service or calibration. */
function occupyingOptimisticMode(optimistic: OptimisticModeSwitch): ROBOT_MODES | null {
  if (!optimistic) {
    return null
  }
  const { actionKey, mode } = optimistic
  const isCalibStart = actionKey.startsWith('catalog-calib:') && !actionKey.includes('finish')
  const isReset = actionKey.startsWith('catalog-reset:')
  const isServiceStart = actionKey.startsWith('catalog-service:') && !actionKey.includes('stop')
  if (isCalibStart || isReset || isServiceStart) {
    return mode
  }
  return null
}

/**
 * Deployment and firmware controls must stay inactive while a service action
 * or service-point calibration (including reset) is running or being sent.
 */
export function deploymentTasksBlockedReason(params: {
  activeMode?: ROBOT_MODES | null
  pendingMode?: ROBOT_MODES | null
  optimistic?: OptimisticModeSwitch
  catalogOperation?: RobotCatalogOperation | null
}): string | null {
  return (
    reasonForMode(params.activeMode) ??
    reasonForMode(params.pendingMode) ??
    reasonForMode(occupyingOptimisticMode(params.optimistic)) ??
    reasonForCatalogOperation(params.catalogOperation)
  )
}
